import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Input, Modal, Typography, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import { globalSearch } from '../../../services/commonService';
import dayjs from 'dayjs';
import Loader from '../Loader';
import { useNavigate } from 'react-router-dom';
import { getCanonicalUrl, STEP_VALUES } from '../../../features/NewIdeationXPLR/shared/urlUtils';
import { getCategoryFromField } from '../../../features/DataXPLR/utils';

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
}

// const STATIC_SEARCH_DATA = [
//   {
//     id: 1,
//     title: 'Record2Report Standardisation',
//     subtitle: 'CRX - 3890 \u2022 Ideation',
//     date: '08/07/25',
//     type: 'Ideation',
//   },
//   {
//     id: 2,
//     title: 'Agfa - Record2Report Standardisation Workshop',
//     subtitle: 'CRX - 890 \u2022 Opportunity',
//     date: '08/07/25',
//     type: 'Opportunity',
//   },
//   {
//     id: 3,
//     title: 'Agfa - Source2Contract Governance Session',
//     subtitle: 'CRX - 8193 \u2022 Ideation',
//     date: '08/07/25',
//     type: 'Ideation',
//   },
//   {
//     id: 4,
//     title: 'Source2Contract',
//     subtitle: 'CRX - 890 \u2022 Solution',
//     date: '08/07/25',
//     type: 'Solution',
//   },
//   {
//     id: 5,
//     title: 'Agfa - Source2Contract Governance Session',
//     subtitle: 'CRX - 8193 \u2022 Ideation',
//     date: '08/07/25',
//     type: 'Ideation',
//   },
//   {
//     id: 6,
//     title: 'Source2Contract',
//     subtitle: 'CRX - 890 \u2022 Solution',
//     date: '08/07/25',
//     type: 'Solution',
//   },
// ];

const highlightText = (text: string, searchQuery: string): React.ReactNode => {
  if (!searchQuery || !text) {
    return text;
  }

  const query = searchQuery.trim();
  if (!query) {
    return text;
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const testRegex = new RegExp(`^${escapedQuery}$`, 'i');
        return testRegex.test(part) ? (
          <strong key={index}>{part}</strong>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        );
      })}
    </>
  );
};

const getIconForType = (type: string) => {
  const normalizedType = type?.toLowerCase();
  switch (normalizedType) {
    case 'ideation':
      return (
        <svg
          width="15"
          height="15"
          viewBox="0 0 17 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.0319 13.8564H3.77288C3.7014 13.8564 3.64303 13.8095 3.63112 13.7404C3.48935 13.1252 3.10098 12.5346 2.52321 12.0589C0.919714 10.7605 3.42093e-05 8.847 3.42093e-05 6.79394C-0.0118787 3.04899 3.08912 0 6.89641 0C8.07581 0 9.21946 0.289432 10.2093 0.834303C10.2677 0.869458 10.3035 0.938594 10.2796 1.0089C10.2332 1.13546 10.2213 1.2292 10.2213 1.33348V1.4612C10.2213 1.4612 10.1974 1.55377 10.1498 1.58776C10.1033 1.61119 10.0557 1.61119 10.0092 1.58776L9.87935 1.51862C9.63156 1.37918 9.34802 1.3452 9.06568 1.41433C8.79406 1.48347 8.5701 1.65806 8.44143 1.9018L7.10836 4.1622C6.96778 4.40594 6.93204 4.68366 7.00233 4.96255C7.07381 5.218 7.25012 5.43829 7.49791 5.57658L7.61585 5.64689C7.61585 5.64689 7.68614 5.71602 7.68614 5.76289C7.68614 5.80859 7.66231 5.85546 7.61585 5.89062L7.49791 5.97147C7.23821 6.11092 7.06189 6.33121 7.00233 6.5972C6.93204 6.87609 6.96778 7.15381 7.10836 7.39755L8.44143 9.65912C8.582 9.90168 8.79404 10.0646 9.06568 10.1454C9.33729 10.2157 9.61963 10.1806 9.86742 10.0411L10.0092 9.97199C10.0092 9.97199 10.1033 9.94855 10.1498 9.97199C10.1974 9.99543 10.2212 10.0411 10.2212 10.0997V10.2274C10.2212 10.7946 10.693 11.2703 11.2589 11.2703H11.7771C11.8354 11.2703 11.895 11.3055 11.9188 11.3629C11.9415 11.4215 11.9188 11.4789 11.8831 11.5258C11.671 11.7344 11.4709 11.9195 11.2696 12.0707C10.693 12.5347 10.3153 13.1148 10.1736 13.7522C10.1616 13.8214 10.1033 13.8682 10.0318 13.8682L10.0319 13.8564ZM6.89642 20C5.14044 20 3.70121 18.5857 3.70121 16.8466V16.4752H3.58446C3.13652 16.4752 2.77079 16.1154 2.77079 15.6748C2.77079 15.2342 3.14725 14.8745 3.58446 14.8745H10.2094C10.6574 14.8745 11.0231 15.2342 11.0231 15.6748C11.0231 16.1154 10.6693 16.4752 10.2094 16.4752H10.1034V16.8466C10.1034 18.5856 8.66564 20 6.89642 20ZM15.9862 5.20621C15.786 5.32222 15.6562 5.55425 15.6562 5.77452V5.78624C15.6562 6.02997 15.786 6.25028 15.9862 6.36628L17 6.93459L15.7968 8.97469L14.8187 8.40638H14.7949C14.5828 8.29037 14.3231 8.29037 14.123 8.40638C13.9109 8.52239 13.793 8.74269 13.793 8.97469V10.123H11.3997V8.97469C11.3997 8.73096 11.271 8.52238 11.0697 8.40638C10.8576 8.29037 10.5979 8.29037 10.3978 8.40638L9.384 8.97469L8.18197 6.93459L9.19577 6.36628C9.39591 6.25027 9.52575 6.01824 9.52575 5.78624C9.52575 5.55422 9.3959 5.33393 9.19577 5.21793L8.18197 4.64962L9.384 2.60952L10.3978 3.17666C10.5979 3.29267 10.8576 3.29267 11.0697 3.17666C11.271 3.06065 11.3997 2.84035 11.3997 2.60952V1.46116H13.793V2.60952C13.793 2.62006 13.793 2.63178 13.8049 2.6435C13.8049 2.85208 13.9109 3.02666 14.0884 3.14269C14.0932 3.14855 14.0992 3.1509 14.1051 3.15441C14.1111 3.15676 14.117 3.16027 14.123 3.16613C14.3231 3.28214 14.5828 3.28214 14.7949 3.16613L15.7968 2.59782L17 4.63792L15.9862 5.20621ZM13.5226 8.10524C14.2409 7.82753 14.783 7.23578 14.9843 6.50458C15.0665 6.296 15.0903 6.06397 15.0903 5.83314C15.0903 5.32224 14.9247 4.83595 14.6305 4.42932C14.2064 3.8153 13.4988 3.4087 12.697 3.38641C12.6613 3.3747 12.6255 3.3747 12.591 3.3747C11.2115 3.3747 10.0916 4.47619 10.0916 5.83312C10.0916 7.18888 11.2115 8.29037 12.591 8.29037C12.921 8.29037 13.2271 8.22125 13.5226 8.10524ZM12.5969 7.12095H12.6029H12.5909H12.5969ZM11.2936 5.83314C11.2936 6.53857 11.8798 7.11741 12.5969 7.12095C13.1008 7.11861 13.5583 6.84089 13.7692 6.40148C13.8645 6.21633 13.9109 6.04174 13.9109 5.83317C13.9109 5.48513 13.7692 5.16055 13.5226 4.91799C13.2867 4.68597 12.9448 4.54653 12.6029 4.54653C11.8833 4.54653 11.2936 5.12654 11.2936 5.83314Z"
            fill="#666666"
          />
        </svg>
      );
    case 'ai_opportunity':
    case 'opportunity':
      return (
        <WbIncandescentIcon
          sx={{
            color: '#666666',
            fontSize: '18px',
            transform: 'rotate(180deg)',
            width: 18,
            height: 18,
          }}
        />
      );
    case 'new_solution':
    case 'solution':
      return (
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.2885 4.14734C13.2372 3.19858 13.2372 1.66008 12.2885 0.711462C11.3396 -0.237154 9.80128 -0.237154 8.85322 0.711462L1.98174 7.58294C1.90464 7.66032 1.83441 7.74149 1.76979 7.82531C1.61236 8.00783 1.48578 8.20689 1.39045 8.41787L1.39017 8.41689C1.39017 8.41689 1.38835 8.42095 1.38527 8.42558L0.0409144 12.4146C0.0416153 12.4163 0.0421761 12.417 0.0431574 12.4187C-0.0301582 12.5559 -0.0103924 12.7299 0.104978 12.846C0.219647 12.9603 0.391652 12.9811 0.52833 12.91C0.532395 12.9114 0.53604 12.9124 0.539404 12.9137L4.57484 11.6159C4.78946 11.5194 4.99217 11.3902 5.17721 11.229C5.26034 11.1649 5.34136 11.0944 5.41706 11.0185L12.2885 4.14734Z"
            fill="#848484"
          />
        </svg>
      );
    case 'data_xplr':
    case 'data xplr':
      return (
        <svg
          width="13"
          height="13"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.5024 3.03354C17.5024 4.70955 13.584 6.06797 8.75121 6.06797C3.91837 6.06797 0 4.70951 0 3.03354C0 1.35839 3.91837 0 8.75121 0C13.584 0 17.5024 1.35846 17.5024 3.03354Z"
            fill="#666666"
          />
          <path
            d="M17.5024 14.9691C17.5024 16.3994 13.7591 18.0002 8.75121 18.0002C3.7427 18.0002 0 16.3995 0 14.9691V11.3125C1.87866 12.6917 5.38577 13.4177 8.75121 13.4177C12.1167 13.4177 15.6236 12.6909 17.5024 11.3125V14.9691Z"
            fill="#666666"
          />
          <path
            d="M8.75121 12.0279C3.7427 12.0279 0 10.4272 0 8.99685V5.34375C1.8778 6.72299 5.38577 7.4498 8.75121 7.4498C12.1167 7.4498 15.6236 6.72299 17.5024 5.34375V8.99685C17.5024 10.4271 13.7591 12.0279 8.75121 12.0279Z"
            fill="#666666"
          />
        </svg>
      );
    default:
      return <TipsAndUpdatesOutlinedIcon sx={{ color: '#666666', fontSize: '18px' }} />;
  }
};

const getLabelForType = (type: string) => {
  const normalizedType = type?.toLowerCase();
  switch (normalizedType) {
    case 'ideation':
      return 'Ideation';
    case 'ai_opportunity':
    case 'opportunity':
      return 'Opportunity';
    case 'new_solution':
    case 'solution':
      return 'Solution';
    case 'data_xplr':
    case 'data xplr':
      return 'Data';
    default:
      return type;
  }
};

const GlobalSearchModal = ({ open, onClose }: GlobalSearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const fetchResults = useCallback(
    async (query: string, pageNum: number, append: boolean = false) => {
      try {
        setLoading(true);
        const response = await globalSearch(query, pageNum, 20, 'all');
        const rawResults = response.results || [];

        // Filter out ai_opportunity results
        const filteredResults = rawResults.filter(
          (item: any) => item.type?.toLowerCase() !== 'ai_opportunity'
        );

        setResults((prev) => (append ? [...prev, ...filteredResults] : filteredResults));
        setHasMore(response.has_next);

        // If we filtered everything out but there's more on the backend,
        // fetch the next page automatically to keep the search going.
        if (filteredResults.length === 0 && response.has_next) {
          setPage((prevPage) => prevPage + 1);
        } else {
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error('Failed to fetch search results:', error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Initial fetch and search debouncing
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      setPage(1);
      setResults([]);
      setHasMore(true);
      fetchResults(searchQuery, 1, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, open]);

  // Pagination fetch
  useEffect(() => {
    if (page > 1) {
      fetchResults(searchQuery, page, true);
    }
  }, [page]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setResults([]);
      setPage(1);
      setHasMore(true);
      setIsInitialLoad(true);
    }
  }, [open]);

  const handleResultClick = (item: any) => {
    const type = item.type?.toLowerCase();
    const status = item.meta?.status;
    const id = item.id;
    const meta = item.meta;

    if (type === 'ideation') {
      onClose();
      let step: string = STEP_VALUES.IDEATION_INTAKE;
      let activeStep = 0;
      let targetId = id;

      if (meta?.level2_id) {
        step = STEP_VALUES.PROCESS_DIAGRAM;
        activeStep = 2;
        targetId = meta.level2_id;
      } else if (status === 'proposed_enhancements_generated') {
        step = STEP_VALUES.PROPOSED_ENHANCEMENT;
        activeStep = 1;
      } else if (status === 'draft') {
        step = STEP_VALUES.IDEATION_INTAKE;
        activeStep = 0;
      }

      const search = getCanonicalUrl({
        step,
        activeStep,
        id: targetId,
      });

      navigate({
        pathname: '/ideation-xplr',
        search,
      });
    } else if (type === 'new_solution' || type === 'solution') {
      onClose();
      let step = 'generalInformation';
      let activeStep = 0;

      if (status === 'DATA_SOURCES_DONE') {
        activeStep = 1;
      } else if (status === 'ELABORATED_SUMMARY_GENERATED') {
        step = 'elaboratedSummary';
      } else if (status === 'OPPORTUNITY_REPORT_GENERATED') {
        step = 'opportunityReport';
      }

      const searchParams = new URLSearchParams();
      searchParams.set('step', step);
      if (activeStep !== undefined) searchParams.set('activeStep', String(activeStep));
      searchParams.set('solutionId', id);

      navigate({
        pathname: '/solution-xplr',
        search: `?${searchParams.toString()}`,
      });
    } else if (type === 'data_xplr' || type === 'data xplr') {
      onClose();
      const fieldType = meta?.field_type || meta?.fieldType || item.field_type || 'current_process';
      const category = getCategoryFromField(fieldType);
      const conversation_id = meta?.original_conversation_id || item.original_conversation_id;
      const searchParams = new URLSearchParams();
      if (category && category !== 'All') {
        searchParams.set('selectedCategory', category);
      }

      navigate(
        {
          pathname: `/data-xplr/${conversation_id}`,
          search: searchParams.toString() ? `?${searchParams.toString()}` : '',
        },
        {
          state: {
            sessionFieldType: fieldType,
            conversationType: meta?.conversation_type || item.conversation_type,
            sessionName: item.name || item.title,
          },
        }
      );
    } else {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '50vw',
          height: '50vh',
          backgroundColor: 'white',
          borderRadius: '10px',
          px: 2,
          py: 1,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 24,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1 }}>
          <SearchIcon sx={{ color: '#666666' }} />
          <Input
            disableUnderline
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');
              setTimeout(() => {
                setSearchQuery(pastedText);
              }, 0);
            }}
            sx={{ width: '100%', fontFamily: 'Poppins' }}
            autoFocus
          />
          <Box
            onClick={onClose}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg
              width={14}
              height={14}
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1="1"
                y1="1"
                x2="13"
                y2="13"
                stroke="#B8B8B8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <line
                x1="13"
                y1="1"
                x2="1"
                y2="13"
                stroke="#B8B8B8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflowY: 'auto',
            flex: 1,
            maxHeight: 'calc(50vh - 80px)',
            px: 0.5,
            py: 1,
            '&::-webkit-scrollbar': {
              width: '9px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#B8B8B8',
              borderRadius: '4px',
              minHeight: 143,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#9ca3af',
            },
          }}
        >
          {loading && results.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
              }}
            >
              <Loader />
            </Box>
          ) : results.length === 0 && !isInitialLoad ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#737373',
                  fontFamily: 'Poppins',
                  textAlign: 'center',
                  px: 4,
                }}
              >
                {searchQuery.trim() === ''
                  ? 'Start typing to search across Solution, Ideation, and Data XPLR.'
                  : 'No results found'}
              </Typography>
            </Box>
          ) : (
            <>
              {results.map((item, index) => {
                const isLastItem = index === results.length - 1;
                return (
                  <Box
                    key={item.id}
                    ref={isLastItem ? lastElementRef : null}
                    onClick={() => handleResultClick(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#F4F7FF',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{getIconForType(item.type)}</Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#131426',
                            maxWidth: '36vw',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            fontFamily: 'Poppins',
                          }}
                        >
                          {highlightText(item.name || item.title, searchQuery)}
                        </Typography>
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{
                            fontSize: '12px',
                            color: '#737373',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '38vw',
                            fontFamily: 'Poppins',
                          }}
                        >
                          {item.sequential_id} • {getLabelForType(item.type)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#AAA9AE',
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        fontFamily: 'Poppins',
                      }}
                    >
                      Updated on {dayjs(item.updated_at).format('DD/MM/YY')}
                    </Typography>
                  </Box>
                );
              })}
              {loading && results.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#0052CC' }} />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default GlobalSearchModal;
